import { Box } from '@mui/material';
import { BoxProps } from '@mui/system';
import * as React from 'react';
import { CerebroSelectableIcon, CerebroSelectableIconProps } from '.';

const Context = React.createContext<IconMultiSelectContext | null>(null);

export const IconMultiSelect: React.FC<IconMultiSelectProps> = ({
	name,
	error,
	value = [],
	onChange,
	children,
	...props
}) => {
	const context: IconMultiSelectContext = {
		name,
		error,
		selectedValues: value ?? [],
		onSelectedValuesChange: React.useCallback(
			newSelectedValues => onChange({ target: { name, value: newSelectedValues } }),
			[name, onChange]
		),
	};

	return (
		<Context.Provider value={context}>
			<Box {...props}>{children}</Box>
		</Context.Provider>
	);
};
function useIconMultiSelect() {
	var context = React.useContext(Context);

	if (context == null) throw Error('You tried to use an <IconMultiSelectOption/> outside of an <IconMultiSelect/>!');

	return context;
}

export const IconMultiSelectOption: React.FC<IconMultiSelectOptionProps> = ({ value, ...props }) => {
	const { name, error, selectedValues, onSelectedValuesChange } = useIconMultiSelect();

	const indexOfValue = selectedValues.indexOf(value);
	const selected = indexOfValue >= 0;

	const onChange = () => {
		if (selected) selectedValues.splice(indexOfValue, 1);
		else selectedValues.push(value);

		onSelectedValuesChange(selectedValues);
	};

	return <CerebroSelectableIcon name={name} error={error} value={selected} onChange={onChange} {...props} />;
};

// Like a partial React.ChangeEvent because formik falls back to handling any value it receives on change as an event if it isn't a string.
export interface IconMultiSelectChangeEvent {
	target: {
		name: string;
		value: string[];
	};
}

interface IconMultiSelectProps extends Omit<BoxProps, 'onChange'> {
	name: string;
	value?: string[] | null;
	error: boolean;
	onChange: (event: IconMultiSelectChangeEvent) => void;
}

interface IconMultiSelectContext {
	name: string;
	error: boolean;
	selectedValues: string[];
	onSelectedValuesChange: (newSelectedValues: string[]) => void;
}

type IconMultiSelectOptionProps = Omit<CerebroSelectableIconProps, 'value' | 'onChange' | 'name' | 'error'> & {
	value: string;
};
